import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { extendTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import {
  SUPERADMIN_COMPANIES_SHORT_NAME,
  SUPERADMIN_USERS_SHORT_NAME,
  SUPERADMIN_FEATURES_SHORT_NAME,
  SUPERADMIN_CATEGORIES_SHORT_NAME,
  SUPERADMIN_TEMPLATES_SHORT_NAME,
} from "../../Constants";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMediaQuery, useTheme } from "@mui/material";
import { AppProvider } from "@toolpad/core/AppProvider";
import { DashboardLayout } from "@toolpad/core/DashboardLayout";
import Grid from "@mui/material/Grid2";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import GroupIcon from "@mui/icons-material/Group";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import BrushIcon from "@mui/icons-material/Brush";
import DescriptionIcon from "@mui/icons-material/Description";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import PersonIcon from "@mui/icons-material/Person";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import Dashboard from "../Pages/Common/Dashboard";
import Companies from "../Pages/SuperAdmin/Companies";
import Templates from "../Pages/SuperAdmin/Templates";
import ChangePassword from "../Pages/Common/ChangePassword";
import Support from "../Pages/OrgAdmin/Support";
import { logout } from "../Utils/AuthService";
import { useNavigate } from "react-router-dom";
import Users from "../Pages/SuperAdmin/Users";
import { fetchFeatures, resetAppState } from "../../redux/slices/appSlice";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import defaultAdmin from "../../Assets/profile.png";
import Categories from "../Pages/SuperAdmin/Categories";

const demoTheme = extendTheme({
  colorSchemes: { light: true, dark: true },
  colorSchemeSelector: "class",
  colorScheme: "light",
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 600,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ".css-14tatqh-MuiTypography-root": {
          display: "none !important",
        },
        ".css-yzjoij": {
          display: "none !important",
        },
        ".css-f84ov1-MuiTypography-root": {
          display: "none !important",
        },
        ".css-23htwk": {
          background: "#f5f5f5",
        },
      },
    },
  },
});

const NAVIGATION = [
  {
    segment: "dashboard",
    title: "Dashboard",
    icon: <DashboardIcon />,
  },
  {
    kind: "divider",
  },
  {
    segment: "companies",
    title: "Companies",
    icon: <BusinessCenterIcon />,
  },
  {
    segment: "users",
    title: "Users",
    icon: <GroupIcon />,
  },
  {
    segment: "themes",
    title: "Themes",
    icon: <BrushIcon />,
    children: [
      {
        segment: "categories",
        title: "Categories",
        icon: <DescriptionIcon />,
      },
      {
        segment: "templates",
        title: "Templates",
        icon: <ViewQuiltIcon />,
      },
    ],
  },
  {
    kind: "divider",
  },
  {
    kind: "header",
    title: "Account",
  },
  {
    segment: "profile",
    title: "Profile",
    icon: <PersonIcon />,
  },
  {
    segment: "support",
    title: "Support",
    icon: <SupportAgentIcon />,
  },
];

function CustomAppTitle() {
  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Typography variant="h6">SuperAdmin</Typography>
      <Tooltip title="Connected to production">
        <CheckCircleIcon color="success" fontSize="small" />
      </Tooltip>
    </Stack>
  );
}

function SidebarFooter({ mini }) {
  return (
    <Typography
      variant="caption"
      sx={{ m: 1, whiteSpace: "nowrap", overflow: "hidden" }}
    >
      {mini ? "© 2025" : `© WebTronic ${new Date().getFullYear()}`}
    </Typography>
  );
}

SidebarFooter.propTypes = {
  mini: PropTypes.bool.isRequired,
};

function useDemoRouter(initialPath) {
  const [pathname, setPathname] = React.useState(initialPath);

  const router = React.useMemo(() => {
    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (path) => setPathname(String(path)),
    };
  }, [pathname]);

  return router;
}

const DashboardLayoutBasic = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery("(min-width:1200px) and (max-width:1600px)");
  const isLargeDesktop = useMediaQuery(
    "(min-width:1600px) and (max-width:2400px)"
  );

  const gridWidth = isMobile
    ? "100vw"
    : isLargeDesktop
      ? "85vw"
      : isDesktop
        ? "79vw"
        : "100vw";

  const router = useDemoRouter("/dashboard");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const features = useSelector((state) => state.app.features);
  const { firstName, email } = useSelector((state) => state.app);

  useEffect(() => {
    if (!features || features.length === 0) {
      dispatch(fetchFeatures());
    }
  }, [dispatch, features]);

  const filterNavigation = (navigation, features) => {
    return navigation
      .map((item) => {
        if (item.segment && !item.children) {
          switch (item.segment) {
            case "companies":
              return features?.some(
                (feature) =>
                  feature.shortName === SUPERADMIN_COMPANIES_SHORT_NAME
              )
                ? item
                : null;
            case "users":
              return features?.some(
                (feature) => feature.shortName === SUPERADMIN_USERS_SHORT_NAME
              )
                ? item
                : null;
            default:
              return item;
          }
        }

        if (item.segment === "themes" && item.children) {
          const filteredChildren = item.children.filter((child) => {
            switch (child.segment) {
              case "categories":
                return features?.some(
                  (feature) =>
                    feature.shortName === SUPERADMIN_CATEGORIES_SHORT_NAME
                );
              case "templates":
                return features?.some(
                  (feature) =>
                    feature.shortName === SUPERADMIN_TEMPLATES_SHORT_NAME
                );
              default:
                return false;
            }
          });

          return filteredChildren.length > 0
            ? { ...item, children: filteredChildren }
            : null;
        }

        return item;
      })
      .filter(Boolean);
  };

  const filteredNavigation = filterNavigation(NAVIGATION, features);

  const renderContent = () => {
    switch (router.pathname) {
      case "/dashboard":
        return <Dashboard />;
      case "/companies":
        return <Companies />;
      case "/users":
        return <Users />;
      case "/themes/categories":
        return <Categories />;
      case "/themes/templates":
        return <Templates />;
      case "/profile":
        return <ChangePassword />;
      case "/support":
        return <Support />;
      case "/logout":
        handleLogout();
        break;
      default:
        return null;
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const [session, setSession] = React.useState({
    user: {
      name: firstName || "Elio",
      email: email || "elio.mouhasseb3@gmail.com",
      image: defaultAdmin,
    },
  });

  const authentication = React.useMemo(() => {
    return {
      signOut: () => {
        dispatch(resetAppState());
        handleLogout();
      },
    };
  }, []);

  return (
    <AppProvider
      navigation={filteredNavigation}
      router={router}
      theme={demoTheme}
      session={session}
      authentication={authentication}
    >
      <DashboardLayout
        style={{
          margin: 0,
          padding: 0,
          width: "100vw",
          height: "100vh",
          boxSizing: "border-box",
        }}
        slots={{
          appTitle: CustomAppTitle,
          sidebarFooter: SidebarFooter,
        }}
      >
        <Grid
          container
          spacing={0}
          style={{
            width: gridWidth,
            height: "100vh",
            margin: 0,
            padding: 0,
          }}
        >
          <Grid
            item
            xs={12}
            style={{
              width: "100%",
              margin: 0,
              padding: 0,
            }}
          >
            {renderContent()}
          </Grid>
        </Grid>
      </DashboardLayout>
    </AppProvider>
  );
};

export default DashboardLayoutBasic;
